/**
 * Policy Editor Store
 * 
 * Manages the state of the policy editor including:
 * - Current code and dirty state
 * - Validation issues
 * - AI assistance state
 * - View mode (edit/diff/preview)
 * 
 * Peer reviewed: Gemini 3 Pro, GPT-4o
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  ValidationIssue,
  AIAssistanceRequest,
  AIAssistanceResponse,
  PolicyVersion,
  EditorState,
} from '../types/policy';

// ============================================================================
// State Interface
// ============================================================================

interface EditorStoreState extends EditorState {
  /** Version history */
  versions: PolicyVersion[];
  
  /** Templates sidebar open */
  isTemplatesPanelOpen: boolean;
  
  /** AI assistant panel open */
  isAIPanelOpen: boolean;
  
  /** History panel open */
  isHistoryPanelOpen: boolean;
  
  /** Minimap visible */
  isMinimapVisible: boolean;
  
  /** Word wrap enabled */
  isWordWrapEnabled: boolean;
  
  /** Font size */
  fontSize: number;
}

// ============================================================================
// Actions Interface
// ============================================================================

interface EditorActions {
  // Code editing
  setCode: (code: string) => void;
  setOriginalCode: (code: string) => void;
  resetCode: () => void;
  
  // Policy management
  setCurrentPolicy: (policyId: string | null, code: string) => void;
  
  // Cursor/Selection
  setCursorPosition: (line: number, column: number) => void;
  setSelection: (startLine: number, endLine: number, startColumn: number, endColumn: number) => void;
  clearSelection: () => void;
  
  // Validation
  setValidationIssues: (issues: ValidationIssue[]) => void;
  addValidationIssue: (issue: ValidationIssue) => void;
  clearValidationIssues: () => void;
  setIsValidating: (isValidating: boolean) => void;
  
  // AI Assistance
  startAIRequest: (request: AIAssistanceRequest) => void;
  appendAIStream: (content: string) => void;
  completeAIRequest: (response: AIAssistanceResponse) => void;
  cancelAIRequest: () => void;
  clearAIState: () => void;
  
  // View mode
  setViewMode: (mode: EditorState['viewMode']) => void;
  setCompareVersionId: (versionId: string | null) => void;
  
  // Versions
  setVersions: (versions: PolicyVersion[]) => void;
  addVersion: (version: PolicyVersion) => void;
  
  // UI
  toggleTemplatesPanel: () => void;
  toggleAIPanel: () => void;
  toggleHistoryPanel: () => void;
  setMinimapVisible: (visible: boolean) => void;
  setWordWrapEnabled: (enabled: boolean) => void;
  setFontSize: (size: number) => void;
  
  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

// Using Partial to allow omitting optional fields
const initialState: Omit<EditorStoreState, 'selection'> & { selection?: EditorState['selection'] } = {
  currentPolicyId: null,
  code: '',
  originalCode: '',
  isDirty: false,
  cursorPosition: { line: 1, column: 1 },
  // selection is omitted (undefined is implied for optional fields)
  validationIssues: [],
  isValidating: false,
  aiState: {
    isLoading: false,
    currentRequest: null,
    streamContent: '',
    lastResponse: null,
  },
  viewMode: 'edit',
  compareVersionId: null,
  versions: [],
  isTemplatesPanelOpen: false,
  isAIPanelOpen: true,
  isHistoryPanelOpen: false,
  isMinimapVisible: true,
  isWordWrapEnabled: false,
  fontSize: 14,
};

// ============================================================================
// Store Creation
// ============================================================================

export const useEditorStore = create<EditorStoreState & EditorActions>()(
  devtools(
    subscribeWithSelector(
      immer((set) => ({
        ...initialState,

        // ----------------------------------------------------------------------
        // Code Editing
        // ----------------------------------------------------------------------
        
        setCode: (code: string) => {
          set((state) => {
            state.code = code;
            state.isDirty = code !== state.originalCode;
          });
        },
        
        setOriginalCode: (code: string) => {
          set((state) => {
            state.originalCode = code;
            state.isDirty = state.code !== code;
          });
        },
        
        resetCode: () => {
          set((state) => {
            state.code = state.originalCode;
            state.isDirty = false;
          });
        },

        // ----------------------------------------------------------------------
        // Policy Management
        // ----------------------------------------------------------------------
        
        setCurrentPolicy: (policyId: string | null, code: string) => {
          set((state) => {
            state.currentPolicyId = policyId;
            state.code = code;
            state.originalCode = code;
            state.isDirty = false;
            state.validationIssues = [];
            state.versions = [];
            state.compareVersionId = null;
            state.viewMode = 'edit';
          });
        },

        // ----------------------------------------------------------------------
        // Cursor/Selection
        // ----------------------------------------------------------------------
        
        setCursorPosition: (line: number, column: number) => {
          set((state) => {
            state.cursorPosition = { line, column };
          });
        },
        
        setSelection: (startLine: number, endLine: number, startColumn: number, endColumn: number) => {
          set((state) => {
            state.selection = { startLine, endLine, startColumn, endColumn };
          });
        },
        
        clearSelection: () => {
          set((state) => {
            delete state.selection;
          });
        },

        // ----------------------------------------------------------------------
        // Validation
        // ----------------------------------------------------------------------
        
        setValidationIssues: (issues: ValidationIssue[]) => {
          set((state) => {
            state.validationIssues = issues;
          });
        },
        
        addValidationIssue: (issue: ValidationIssue) => {
          set((state) => {
            state.validationIssues.push(issue);
          });
        },
        
        clearValidationIssues: () => {
          set((state) => {
            state.validationIssues = [];
          });
        },
        
        setIsValidating: (isValidating: boolean) => {
          set((state) => {
            state.isValidating = isValidating;
          });
        },

        // ----------------------------------------------------------------------
        // AI Assistance
        // ----------------------------------------------------------------------
        
        startAIRequest: (request: AIAssistanceRequest) => {
          set((state) => {
            state.aiState.isLoading = true;
            state.aiState.currentRequest = request;
            state.aiState.streamContent = '';
            state.aiState.lastResponse = null;
          });
        },
        
        appendAIStream: (content: string) => {
          set((state) => {
            state.aiState.streamContent += content;
          });
        },
        
        completeAIRequest: (response: AIAssistanceResponse) => {
          set((state) => {
            state.aiState.isLoading = false;
            state.aiState.lastResponse = response;
          });
        },
        
        cancelAIRequest: () => {
          set((state) => {
            state.aiState.isLoading = false;
            state.aiState.currentRequest = null;
          });
        },
        
        clearAIState: () => {
          set((state) => {
            state.aiState = {
              isLoading: false,
              currentRequest: null,
              streamContent: '',
              lastResponse: null,
            };
          });
        },

        // ----------------------------------------------------------------------
        // View Mode
        // ----------------------------------------------------------------------
        
        setViewMode: (mode: EditorState['viewMode']) => {
          set((state) => {
            state.viewMode = mode;
          });
        },
        
        setCompareVersionId: (versionId: string | null) => {
          set((state) => {
            state.compareVersionId = versionId;
            if (versionId) {
              state.viewMode = 'diff';
            }
          });
        },

        // ----------------------------------------------------------------------
        // Versions
        // ----------------------------------------------------------------------
        
        setVersions: (versions: PolicyVersion[]) => {
          set((state) => {
            state.versions = versions;
          });
        },
        
        addVersion: (version: PolicyVersion) => {
          set((state) => {
            state.versions.unshift(version);
          });
        },

        // ----------------------------------------------------------------------
        // UI
        // ----------------------------------------------------------------------
        
        toggleTemplatesPanel: () => {
          set((state) => {
            state.isTemplatesPanelOpen = !state.isTemplatesPanelOpen;
          });
        },
        
        toggleAIPanel: () => {
          set((state) => {
            state.isAIPanelOpen = !state.isAIPanelOpen;
          });
        },
        
        toggleHistoryPanel: () => {
          set((state) => {
            state.isHistoryPanelOpen = !state.isHistoryPanelOpen;
          });
        },
        
        setMinimapVisible: (visible: boolean) => {
          set((state) => {
            state.isMinimapVisible = visible;
          });
        },
        
        setWordWrapEnabled: (enabled: boolean) => {
          set((state) => {
            state.isWordWrapEnabled = enabled;
          });
        },
        
        setFontSize: (size: number) => {
          set((state) => {
            state.fontSize = Math.max(10, Math.min(24, size));
          });
        },

        // ----------------------------------------------------------------------
        // Reset
        // ----------------------------------------------------------------------
        
        reset: () => {
          set(() => ({ ...initialState }));
        },
      }))
    ),
    { name: 'editor-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectIsDirty = (state: EditorStoreState & EditorActions) => state.isDirty;
export const selectCode = (state: EditorStoreState & EditorActions) => state.code;
export const selectValidationIssues = (state: EditorStoreState & EditorActions) => state.validationIssues;
export const selectIsValidating = (state: EditorStoreState & EditorActions) => state.isValidating;
export const selectViewMode = (state: EditorStoreState & EditorActions) => state.viewMode;
export const selectAIState = (state: EditorStoreState & EditorActions) => state.aiState;
export const selectVersions = (state: EditorStoreState & EditorActions) => state.versions;

export const selectErrorCount = (state: EditorStoreState & EditorActions) =>
  state.validationIssues.filter((i) => i.severity === 'error').length;

export const selectWarningCount = (state: EditorStoreState & EditorActions) =>
  state.validationIssues.filter((i) => i.severity === 'warning').length;

export const selectHasErrors = (state: EditorStoreState & EditorActions) =>
  state.validationIssues.some((i) => i.severity === 'error');

export const selectCurrentVersion = (state: EditorStoreState & EditorActions) =>
  state.versions.find((v) => v.id === state.compareVersionId) ?? null;

